// src/pages/reports/fgi/PersonnelBenefits.js
import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Fade from "@mui/material/Fade";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SavingsIcon from "@mui/icons-material/Savings";
import CachedIcon from "@mui/icons-material/Cached";
import { motion, AnimatePresence } from "framer-motion";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import { getGovernmentBenefitsPersonnelApi } from "../../../api/government";
import MbsModeDialog from "./dialog/MbsModeDialog";

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  const num = Number(value);
  if (!Number.isNaN(num)) {
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return value;
};

const PersonnelBenefits = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const searchParams = new URLSearchParams(location.search);
  const initialGroup = searchParams.get("group") || "";
  const initialStore = searchParams.get("store") || "";
  const initialMonth = searchParams.get("month") || "";

  const [groupName, setGroupName] = useState(initialGroup);
  const [storeName, setStoreName] = useState(initialStore);
  const [selectedMonthParam, setSelectedMonthParam] = useState(initialMonth);
  const [summary, setSummary] = useState({});
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [firstCutoffRange, setFirstCutoffRange] = useState("");
  const [secondCutoffRange, setSecondCutoffRange] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mbsMode, setMbsMode] = useState("normal");
  const [mbsModeDialogOpen, setMbsModeDialogOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const headCellBaseSx = {
    bgcolor: "#f8f9fa",
    fontWeight: "bold",
    borderBottom: "2px solid #e5e7eb",
    borderRight: "1px solid #e0e0e0",
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  const handleBackToStores = () => {
    if (groupName && selectedMonthParam) {
      navigate(
        `/reports/government-benefits/store?group=${encodeURIComponent(
          groupName
        )}&month=${encodeURIComponent(selectedMonthParam)}`
      );
    } else {
      navigate("/reports/government-benefits");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!initialStore || !initialMonth) {
        setError("Missing store or month parameter in URL.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const json = await getGovernmentBenefitsPersonnelApi({
          month: initialMonth,
          store: initialStore,
          mbsMode,
        });

        let payload = json;
        if (payload && typeof payload === "object" && payload.data) {
          payload = payload.data;
        }

        if (json?.status === "success" || payload.summary) {
          setGroupName(payload.group || initialGroup);
          setStoreName(payload.store || initialStore);
          setSelectedMonthParam(payload.month || initialMonth);
          setSummary(payload.summary || {});
          setTotalEmployees(
            typeof payload.totalEmployees === "number"
              ? payload.totalEmployees
              : Object.keys(payload.summary || {}).length
          );
          setFirstCutoffRange(payload.first_cutoff_range || "N/A");
          setSecondCutoffRange(
            payload.second_cutoff_range || "No 2nd Cutoff Yet"
          );

          enqueueSnackbar("Personnel-level government benefits loaded", {
            variant: "success",
          });
        } else {
          setError(
            json?.message ||
              "Failed to load personnel-level government benefits."
          );
          setSummary({});
          setTotalEmployees(0);
          enqueueSnackbar(
            "Failed to load personnel-level government benefits.",
            {
              variant: "error",
            }
          );
        }
      } catch (err) {
        console.error(
          "Error loading personnel-level government benefits:",
          err
        );
        setError("Failed to load personnel-level government benefits.");
        setSummary({});
        setTotalEmployees(0);
        enqueueSnackbar("Failed to load personnel-level government benefits.", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [initialGroup, initialStore, initialMonth, enqueueSnackbar, mbsMode]);

  const handleMbsModeSelect = (nextMode) => {
    setMbsMode(nextMode);
    setPage(0);
    setMbsModeDialogOpen(false);
  };

  const rows = useMemo(
    () =>
      Object.entries(summary).map(([employee, row]) => ({
        employee,
        ...row,
        sss: row.sss || {},
        philhealth: row.philhealth || {},
        pagibig: row.pagibig || {},
      })),
    [summary]
  );

  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const totals = useMemo(() => {
    const acc = {
      totalGross1: 0,
      totalGross2: 0,
      totalGrossAll: 0,
      totalSss1: 0,
      totalSss1_er: 0,
      totalSss2: 0,
      totalSss2_er: 0,
      totalSssAll: 0,
      totalSssAll_er: 0,
      totalEc: 0,
      totalMsc: 0,
      totalPhil1: 0,
      totalPhil1_er: 0,
      totalPhil2: 0,
      totalPhil2_er: 0,
      totalPhilAll: 0,
      totalPhilAll_er: 0,
      totalMbs: 0,
      totalPagibig1: 0,
      totalPagibig2: 0,
      totalPagibigAll: 0,
      totalSssSalaryLoan: 0,
      totalSssCalamityLoan: 0,
      totalPagibigMplLoan: 0,
      totalPagibigCalamityLoan: 0,
    };

    rows.forEach((row) => {
      const sss = row.sss || {};
      const ph = row.philhealth || {};
      const pi = row.pagibig || {};

      acc.totalGross1 += Number(row.gross_cutoff1 || 0);
      acc.totalGross2 += Number(row.gross_cutoff2 || 0);
      acc.totalGrossAll += Number(row.gross_pay || 0);

      acc.totalSss1 += Number(sss.cutoff1 || 0);
      acc.totalSss1_er += Number(sss.cutoff1_er || 0);
      acc.totalSss2 += Number(sss.cutoff2 || 0);
      acc.totalSss2_er += Number(sss.cutoff2_er || 0);
      acc.totalSssAll += Number(sss.overall || 0);
      acc.totalSssAll_er += Number(sss.overall_er || 0);

      acc.totalEc += Number(row.ec || 0);
      acc.totalMsc += Number(row.msc || 0);

      acc.totalPhil1 += Number(ph.cutoff1 || 0);
      acc.totalPhil1_er += Number(ph.cutoff1_er || 0);
      acc.totalPhil2 += Number(ph.cutoff2 || 0);
      acc.totalPhil2_er += Number(ph.cutoff2_er || 0);
      acc.totalPhilAll += Number(ph.overall || 0);
      acc.totalPhilAll_er += Number(ph.overall_er || 0);
      acc.totalMbs += Number(ph.mbs || 0);

      acc.totalPagibig1 += Number(pi.cutoff1 || 0);
      acc.totalPagibig2 += Number(pi.cutoff2 || 0);
      acc.totalPagibigAll += Number(pi.overall || 0);

      acc.totalSssSalaryLoan += Number(row.sssSalaryLoan || 0);
      acc.totalSssCalamityLoan += Number(row.sssCalamityLoan || 0);
      acc.totalPagibigMplLoan += Number(row.pagibigMplLoan || 0);
      acc.totalPagibigCalamityLoan += Number(row.pagibigCalamityLoan || 0);
    });

    return acc;
  }, [rows]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              mb: 2,
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={handleBackToStores}>
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "#2c3e50",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AssessmentIcon sx={{ color: "#12C1DC" }} />
                Personnel Government Benefits
              </Typography>
            </Box>
            <Chip
              label={`${totalEmployees} Employees`}
              color="info"
              variant="outlined"
            />
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/reports">
              Reports
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/reports/government-benefits"
            >
              Government Benefits Summary
            </Link>
            {groupName && selectedMonthParam ? (
              <Link
                underline="hover"
                color="inherit"
                href={`/reports/government-benefits/store?group=${encodeURIComponent(
                  groupName
                )}&month=${encodeURIComponent(selectedMonthParam)}`}
              >
                Stores
              </Link>
            ) : (
              <Typography color="inherit">Stores</Typography>
            )}
            <Typography color="text.primary">Personnel</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Store Information
              </Typography>
              <Typography variant="body1">
                <strong>Group:</strong> {groupName || "N/A"}
              </Typography>
              <Typography variant="body1">
                <strong>Store:</strong> {storeName || "N/A"}
              </Typography>
              <Typography variant="body1">
                <strong>Employees:</strong> {totalEmployees}
              </Typography>
              <Typography variant="body1">
                <strong>Month:</strong> {selectedMonthParam || "-"}
              </Typography>
              <Typography variant="body1">
                <strong>1st Cutoff Range:</strong> {firstCutoffRange}
              </Typography>
              <Typography variant="body1">
                <strong>2nd Cutoff Range:</strong> {secondCutoffRange}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                p: 2,
                bgcolor: "#f9fafb",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <HomeIcon fontSize="small" />
                Personnel Summary
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              <TableContainer
                sx={{
                  maxHeight: "70vh",
                  overflowX: "auto",
                  overflowY: "auto",
                }}
              >
                <Table
                  stickyHeader
                  size={isMobile ? "small" : "medium"}
                  aria-label="personnel government benefits summary table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        rowSpan={2}
                        sx={{ ...headCellBaseSx, minWidth: 200 }}
                      >
                        Employee
                      </TableCell>

                      <TableCell
                        align="center"
                        colSpan={3}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 3 * 120,
                        }}
                      >
                        Gross Pay
                      </TableCell>

                      <TableCell
                        align="center"
                        colSpan={6}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 8 * 120,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <AccountBalanceIcon fontSize="small" />
                          SSS
                        </Box>
                      </TableCell>

                      <TableCell
                        align="center"
                        colSpan={7}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 7 * 120,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <LocalHospitalIcon fontSize="small" />
                          PhilHealth
                        </Box>
                      </TableCell>

                      <TableCell
                        align="center"
                        colSpan={3}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 3 * 120,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <HomeIcon fontSize="small" />
                          Pag-IBIG
                        </Box>
                      </TableCell>

                      <TableCell
                        align="center"
                        colSpan={4}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 4 * 120,
                          borderRight: "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <SavingsIcon fontSize="small" />
                          Loans
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total
                      </TableCell>

                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (EE)
                      </TableCell>
                      {/* <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (ER)
                      </TableCell> */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (EE)
                      </TableCell>
                      {/* <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (ER)
                      </TableCell> */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        EC
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        MSC
                      </TableCell>

                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 140 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          MBS
                          <Tooltip
                            title={`Change MBS mode (currently ${
                              mbsMode === "phil" ? "PHIL MBS" : "Normal MBS"
                            })`}
                          >
                            <IconButton
                              size="small"
                              aria-label="Change MBS mode"
                              onClick={() => setMbsModeDialogOpen(true)}
                              sx={{ color: "primary.main", p: 0.25 }}
                            >
                              <CachedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total
                      </TableCell>

                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        SSS Salary Loan
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        SSS Calamity Loan
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        HDMF MPL
                      </TableCell>
                      <TableCell
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 120,
                          borderRight: "none",
                        }}
                      >
                        HDMF Calamity Loan
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={26} align="center">
                          No Data Available
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence>
                        {paginatedRows.map((row, index) => {
                          const isEvenRow = index % 2 === 0;
                          const sss = row.sss || {};
                          const ph = row.philhealth || {};
                          const pi = row.pagibig || {};

                          return (
                            <motion.tr
                              key={row.employee}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              transition={{
                                duration: 0.2,
                                delay: index * 0.03,
                              }}
                              style={{
                                display: "table-row",
                                backgroundColor: isEvenRow
                                  ? "#ffffff"
                                  : "#f8f9fa",
                              }}
                              sx={{
                                "&:hover": {
                                  backgroundColor: isEvenRow
                                    ? "#f0f0f0 !important"
                                    : "#e9ecef !important",
                                  transform: "scale(1.005)",
                                  transition: "all 0.2s",
                                },
                                "& > .MuiTableCell-root": {
                                  borderRight: "1px solid #e0e0e0",
                                  borderBottom: "1px solid #e0e0e0",
                                },
                                "& > .MuiTableCell-root:last-child": {
                                  borderRight: "none",
                                },
                              }}
                            >
                              <TableCell sx={{ minWidth: 200 }}>
                                {row.employee}
                                {row.first_month && (
                                  <Chip
                                    size="small"
                                    label="New"
                                    color="success"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </TableCell>

                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.gross_cutoff1)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.gross_cutoff2)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.gross_pay)}
                              </TableCell>

                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff1)}
                              </TableCell>
                              {/* <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff1_er)}
                              </TableCell> */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff2)}
                              </TableCell>
                              {/* <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff2_er)}
                              </TableCell> */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.overall)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.overall_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.ec)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.msc)}
                              </TableCell>

                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.cutoff1)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.cutoff1_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.cutoff2)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.cutoff2_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.overall)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.overall_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(ph.mbs)}
                              </TableCell>

                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pi.cutoff1)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pi.cutoff2)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pi.overall)}
                              </TableCell>

                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.sssSalaryLoan)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.sssCalamityLoan)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.pagibigMplLoan)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(row.pagibigCalamityLoan)}
                              </TableCell>
                            </motion.tr>
                          );
                        })}
                      </AnimatePresence>
                    )}

                    {rows.length > 0 && (
                      <TableRow>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Total:
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(totals.totalGross1)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalGross2)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalGrossAll)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(totals.totalSss1)}
                        </TableCell>
                        {/* <TableCell align="right">
                          {formatNumber(totals.totalSss1_er)}
                        </TableCell> */}
                        <TableCell align="right">
                          {formatNumber(totals.totalSss2)}
                        </TableCell>
                        {/* <TableCell align="right">
                          {formatNumber(totals.totalSss2_er)}
                        </TableCell> */}
                        <TableCell align="right">
                          {formatNumber(totals.totalSssAll)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalSssAll_er)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalEc)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalMsc)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(totals.totalPhil1)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPhil1_er)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPhil2)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPhil2_er)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPhilAll)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPhilAll_er)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalMbs)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(totals.totalPagibig1)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPagibig2)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPagibigAll)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(totals.totalSssSalaryLoan)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalSssCalamityLoan)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPagibigMplLoan)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(totals.totalPagibigCalamityLoan)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid #e5e7eb",
                  "& .MuiTablePagination-actions": {
                    color: "#12C1DC",
                  },
                  "& .MuiTablePagination-selectIcon": {
                    color: "#12C1DC",
                  },
                }}
              />
            </Box>
          </Paper>
        </motion.div>
        <MbsModeDialog
          open={mbsModeDialogOpen}
          onClose={() => setMbsModeDialogOpen(false)}
          onSelect={handleMbsModeSelect}
          currentMode={mbsMode}
        />
      </Box>
    </Fade>
  );
};

export default PersonnelBenefits;
